import Color from 'color'
import { Point } from './sketch'

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

export function map(
  val: number,
  oldMin: number,
  oldMax: number,
  newMin: number,
  newMax: number
) {
  return ((val - oldMin) * (newMax - newMin)) / (oldMax - oldMin) + newMin
}

export function randomInt(min: number, max: number) {
  return Math.floor(window.fxrand() * (max - min + 1)) + min
}

export function randomFloat(min: number, max: number, decimals: number = 1) {
  return parseFloat((window.fxrand() * (min - max) + max).toFixed(decimals))
}

export const TO_DEGREE = 180 / Math.PI

export const TO_RAD = Math.PI / 180

export function calculateAspectRatioFit(
  srcWidth: number,
  srcHeight: number,
  maxWidth: number,
  maxHeight: number,
  cover = true
) {
  const fn = cover ? Math.max : Math.min
  const ratio = fn(maxWidth / srcWidth, maxHeight / srcHeight)

  return {
    w: srcWidth * ratio,
    h: srcHeight * ratio,
    ratio,
  }
}

export function getPointsDist(p1: Point, p2: Point) {
  return Math.sqrt(
    (p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y)
  )
}

export function lerp(val1: number, val2: number, t: number) {
  return val1 + (val2 - val1) * t
}

export function getBoundingBox(points: Point[]) {
  let minX = points[0].x
  let maxX = points[0].x
  let minY = points[0].y
  let maxY = points[0].y

  for (let i = 1; i < points.length; i++) {
    if (points[i].x < minX) minX = points[i].x
    if (points[i].x > maxX) maxX = points[i].x
    if (points[i].y < minY) minY = points[i].y
    if (points[i].y > maxY) maxY = points[i].y
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

export function getMidpoint(p1: Point, p2: Point): Point {
  return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }
}

export function subdivide(
  points: Point[],
  closedPath = false,
  val = 1
): Point[] {
  const newPoints: Point[] = []

  for (let i = 0; i < points.length - (!closedPath ? 1 : 0); i++) {
    const startPoint = points[i]
    const endPoint = points[closedPath && i === points.length - 1 ? 0 : i + 1]

    const midPoint = getMidpoint(startPoint, endPoint)

    if (startPoint.color && endPoint.color) {
      midPoint.start = startPoint
      midPoint.end = endPoint

      const distanceBetweenStartAndEnd = getPointsDist(startPoint, endPoint)
      const distanceBetweenMidAndStart = getPointsDist(midPoint, startPoint)

      midPoint.color = lerpColor(
        startPoint.color,
        endPoint.color,
        distanceBetweenMidAndStart / distanceBetweenStartAndEnd
      )
    }

    newPoints.push(midPoint)
  }

  const tempArray = newPoints.reduce(
    (arr: Point[], value: Point, i: number) => {
      return arr.concat(points[i], value)
    },
    []
  )

  !closedPath && tempArray.push(points[points.length - 1])

  val -= 1
  if (val > 0) return subdivide(tempArray, closedPath, val)

  return tempArray
}

export function lerpColor(c1: string, c2: string, p: number) {
  const hslC1 = Color(c1).hsl().object()
  const hslC2 = Color(c2).hsl().object()

  const h = lerp(hslC1.h, hslC2.h, p)
  const s = lerp(hslC1.s, hslC2.s, p)
  const l = lerp(hslC1.l, hslC2.l, p)

  return Color({ h, s, l }).hex()
}

export function generateCircle(
  numberOfSides: number,
  r: number,
  centerX: number,
  centerY: number
) {
  let points: Point[] = []

  for (let i = 1; i <= numberOfSides; i++) {
    points.push({
      x: centerX + r * Math.cos((i * 2 * Math.PI) / numberOfSides),
      y: centerY + r * Math.sin((i * 2 * Math.PI) / numberOfSides),
    })
  }

  return points
}

export function getLineEquation(p1: Point, p2: Point) {
  const m = (p2.y - p1.y) / (p2.x - p1.x)
  const p = p1.y - m * p1.x
  return { m, p }
}

export function quadraticTangent(t: number, p1: Point, cp1: Point, p2: Point) {
  const a = (1 - t) * 2
  const b = t * 2
  const vec: Point = { x: 0, y: 0 }

  vec.x = a * (cp1.x - p1.x) + b * (p2.x - cp1.x)
  vec.y = a * (cp1.y - p1.y) + b * (p2.y - cp1.y)

  const u = Math.sqrt(vec.x * vec.x + vec.y * vec.y)
  vec.x /= u
  vec.y /= u

  return vec
}

export function quadraticPosition(t: number, p1: Point, cp1: Point, p2: Point) {
  let tx1 = p1.x
  let ty1 = p1.y
  let tx2 = cp1.x
  let ty2 = cp1.y
  tx1 += (tx2 - tx1) * t
  ty1 += (ty2 - ty1) * t
  tx2 += (p2.x - tx2) * t
  ty2 += (p2.y - ty2) * t

  return {
    x: tx1 + (tx2 - tx1) * t,
    y: ty1 + (ty2 - ty1) * t,
  }
}

export function randomNormal(options?: any) {
  options = Object.assign({ mean: 0, dev: 1 }, options)

  // The Marsaglia Polar method
  let s
  let u
  let v
  let norm
  let mean = options.mean
  let dev = options.dev

  do {
    // U and V are from the uniform distribution on (-1, 1)
    u = window.fxrand() * 2 - 1
    v = window.fxrand() * 2 - 1

    s = u * u + v * v
  } while (s >= 1)

  // Compute the standard normal variate
  norm = u * Math.sqrt((-2 * Math.log(s)) / s)

  // Shape and scale
  return dev * norm + mean
}
